import React from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import ServiceSchema from "../ServiceSchema";

const RoofWallSheeting = () => {
  return (
    <Layout>
      <SEOHead
        title="Metal Roofing Sheets in Hyderabad | Vasavi Structures"
        description="Vasavi Structures offers durable, high-quality metal roofing and wall sheets for factories, warehouses, farm sheds and poultry sheds in Hyderabad."
        keywords="metal roofing sheets Hyderabad, metal wall sheets, roofing sheets for factories, warehouse roofing sheets, poultry shed roofing"
      />
      <ServiceSchema
        serviceType="Roof & Wall Sheeting"
        description="Standing seam metal roofing and wall sheeting systems for factories, warehouses, farm sheds and poultry sheds."
      />

      <div className="min-h-screen bg-gray-50 font-sans">
        {/* Hero Section */}
        <section
          className="relative pt-32 pb-24 bg-cover bg-center bg-fixed text-white text-center"
          style={{
            backgroundImage: `linear-gradient(rgba(11, 38, 83, 0.88), rgba(11, 38, 83, 0.88)), url('https://images.unsplash.com/photo-1621905252507-b35492cc74ce?q=80&w=2069&auto=format&fit=crop')`,
          }}
        >
          <div className="container-custom">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-8 tracking-tight">
              Roof & Wall Sheeting
            </h1>
            <div className="inline-flex items-center bg-primary/95 px-8 py-4 rounded-full text-lg font-semibold shadow-2xl">
              <Link to="/" className="hover:text-secondary transition">Home</Link>
              <ChevronRight className="mx-4" size={28} />
              <span>Roof & Wall Sheeting</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            {/* Hero Image */}
            <div className="text-center mb-16">
              <img
                src="https://media.istockphoto.com/id/1624809227/photo/3d-blue-metallic-roof-sheet-corrugated-galvanised-iron-for-roof-on-white-background-3d.webp?a=1&b=1&s=612x612&w=0&k=20&c=zoulaydWuRQPCcxuyKcaEMuVWTy5aDk2LON3-a3sbQU="
                alt="Standing Seam Roof Sheeting - Vasavi Structures"
                className="w-full max-w-5xl mx-auto rounded-2xl shadow-2xl"
              />
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-12">
              Advanced Roof & Wall Sheeting Solutions
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground text-center max-w-4xl mx-auto leading-relaxed mb-16">
              At <strong className="text-primary">Vasavi Structures</strong>, we deliver revolutionary 
              <strong> self-supported standing seam roofing systems</strong> that eliminate the need for purlins, trusses, or structural supports. 
              Our state-of-the-art mechanical interlocking technology ensures <strong>100% leak-proof performance</strong> — 
              no holes, no screws, no sealants, no overlaps.
            </p>

            {/* Advantages Section */}
            <div className="grid lg:grid-cols-2 gap-16 items-center my-20">
              <div>
                <img
                  src="https://media.istockphoto.com/id/2243824345/photo/metal-sheet-for-industrial-roof.webp?a=1&b=1&s=612x612&w=0&k=20&c=K5tEB0XYdel09S6N8gAPMZRl6YXGc-j6JnylYnx9A2M="
                  alt="Standing Seam Roofing Installation"
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>

              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-primary mb-10">
                  Why Our Roofing System Stands Above the Rest
                </h3>
                <ul className="space-y-6">
                  {[
                    "100% Leak-Proof – Mechanical sealing with zero perforations",
                    "Zero Maintenance – Built to last 30+ years",
                    "Faster Installation – On-site roll forming saves months",
                    "Superior Thermal & Acoustic Insulation",
                    "Fire Resistant & Fully Corrosion-Proof",
                    "Elegant Modern Appearance with Clean Lines",
                    "Perfect for Low-Slope & Curved Roofs",
                    "Single-Length Sheets – Ridge to Eave, No Joints"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <Check className="w-8 h-8 text-primary flex-shrink-0 mt-1" strokeWidth={3} />
                      <span className="text-lg text-foreground leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Gallery */}
            <div className="my-24">
              <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                Our Roofing Projects
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  "https://plus.unsplash.com/premium_photo-1658506839574-846b58571e9d?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8Um9vZiUyMCUyNiUyMFdhbGwlMjBTaGVldGluZyUyMEhvbWUlMjBSb29mJTIwJTI2JTIwV2FsbCUyMFNoZWV0aW5nfGVufDB8fDB8fHww",
                  "https://images.unsplash.com/photo-1559260160-450dfe5223e9?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fFJvb2YlMjAlMjYlMjBXYWxsJTIwU2hlZXRpbmclMjBIb21lJTIwUm9vZiUyMCUyNiUyMFdhbGwlMjBTaGVldGluZ3xlbnwwfHwwfHx8MA%3D%3D",
                  "https://plus.unsplash.com/premium_photo-1679279576648-96038b99f817?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTN8fFJvb2YlMjAlMjYlMjBXYWxsJTIwU2hlZXRpbmclMjBIb21lJTIwUm9vZiUyMCUyNiUyMFdhbGwlMjBTaGVldGluZ3xlbnwwfHwwfHx8MA%3D%3D",
                  "https://images.unsplash.com/photo-1703681961393-9dbf7d998d99?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTZ8fFJvb2YlMjAlMjYlMjBXYWxsJTIwU2hlZXRpbmclMjBIb21lJTIwUm9vZiUyMCUyNiUyMFdhbGwlMjBTaGVldGluZ3xlbnwwfHwwfHx8MA%3D%3D",
                  "https://media.istockphoto.com/id/1552882590/photo/3d-sea-blue-metallic-stacks-of-corrugated-galvanised-iron-for-roof-sheets-3d-illustration.webp?a=1&b=1&s=612x612&w=0&k=20&c=pEaIEbu9pphgQBnr5HzCk21vD8j7LLAFEz2rv3gBnwM=",
                  "https://images.unsplash.com/photo-1623490879046-75abc98e1176?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Um9vZiUyMCUyNiUyMFdhbGwlMjBTaGVldGluZyUyMEhvbWUlMjBSb29mJTIwJTI2JTIwV2FsbCUyMFNoZWV0aW5nfGVufDB8fDB8fHww",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src + "&auto=format&fit=crop"}
                    alt={`Roof Sheeting Project ${i + 1}`}
                    className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-xl hover:scale-105 transition-transform duration-500"
                  />
                ))}
              </div>
            </div>

            {/* Final CTA Block */}
            <div className="bg-gradient-to-br from-primary/10 via-blue-50 to-primary/5 rounded-3xl p-12 md:p-20 text-center my-20 border border-primary/20">
              <h3 className="text-4xl md:text-5xl font-bold text-primary mb-8">
                The Future of Industrial Roofing is Here
              </h3>
              <p className="text-xl md:text-2xl text-foreground/90 max-w-4xl mx-auto leading-relaxed mb-10">
                Say goodbye to leaks, maintenance, and outdated roofing. 
                Choose <strong>Vasavi Structures</strong> for the strongest, most beautiful, 
                and longest-lasting roofing solution in Hyderabad.
              </p>
              <Link
                to="/enquiry"
                className="btn-primary text-xl px-12 py-5 inline-block hover:scale-105 transition-transform shadow-2xl"
              >
                Get Free Quote Today
              </Link>
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
};

export default RoofWallSheeting;